/**
 * CyberSweep User Filtering Utilities
 *
 * Centralized filter functions for CyberSweep user management.
 * Replaces duplicated filterCyberSweepUsers() functions across components.
 *
 * Part of PE-2047: CASL Permission Model Implementation
 */

import type { User } from '../services/queries/userQueries';
import type { MeetingAttendanceRecord } from '../services/queries/cyberSweepQueries';

/**
 * Check if user has CyberSweep access based on Gobodo roles
 */
export function isCyberSweepUser(user: User): boolean {
  // Primary check: Gobodo role names
  const roleNames = user.roleNames || (user.roles?.map(r => r.name) ?? []);
  const csRoles = ['CyberSweep Team Lead', 'CyberSweep Auditor', 'System Administrator'];

  if (roleNames.some(name => csRoles.includes(name))) {
    return true;
  }

  // Fallback: Check department/title for users without Gobodo roles
  const department = user.department?.toLowerCase() || '';
  const title = user.title?.toLowerCase() || '';

  // Match on department or various title patterns
  return (
    department.includes('cybersweep') ||
    title.includes('auditor') ||
    title.includes('team lead') ||
    title.includes('cs auditor') ||
    title.includes('cybersweep')
  );
}

/**
 * Directory Tab: Show ALL CyberSweep users (active and inactive)
 */
export function filterDirectoryUsers(users: User[]): User[] {
  return users.filter(isCyberSweepUser);
}

/**
 * Meeting Attendance: Show CyberSweep users
 * Note: isActive check removed because user data often has isActive: false
 * even for active users (data reliability issue from Gobodo)
 */
export function filterAttendanceEligibleUsers(users: User[]): User[] {
  return users.filter(isCyberSweepUser);
}

/**
 * Weekly Assignments: Show only users marked PRESENT in attendance
 */
export function filterPresentUsers(
  users: User[],
  attendance: MeetingAttendanceRecord[]
): User[] {
  const presentUserIds = new Set(
    attendance
      .filter(a => a.status === 'present')
      .map(a => a.userId)
  );

  return users.filter(user => presentUserIds.has(user.id));
}

/**
 * Audit Assignment: Check if user can be assigned audits
 */
export function canAssignAudits(user: User): boolean {
  const roleNames = user.roleNames || (user.roles?.map(r => r.name) ?? []);
  const title = user.title?.toLowerCase() || '';

  // Only auditors can be assigned audits (not supervisors/leads)
  return (
    roleNames.includes('CyberSweep Auditor') ||
    title.includes('cs auditor') ||
    title.includes('auditor')
  );
}
